import { useState, useRef, useCallback, useEffect } from 'react';
import type { Proposal } from '../types';

/**
 * Heroicons-outline "hand-raised" path.
 * Matches the stroke-based icon family used in the overview/dashboard.
 */
const HAND_ICON_PATH =
  'M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l-.075 5.925m3.225-5.925a1.575 1.575 0 013.15 0v1.5m-3.15-1.5v5.925m3.15-5.925v3.075M14.25 9.75v1.875a3.375 3.375 0 01-3.375 3.375h-1.5a3.375 3.375 0 01-3.375-3.375V7.875m9.75 0a2.625 2.625 0 114.508 1.838l-2.358 5.652A4.5 4.5 0 0114.4 18H9.6a4.5 4.5 0 01-4.15-2.772L3.092 9.576A2.625 2.625 0 017.5 7.875';

interface InterestCardProps {
  proposal: Proposal;
  /** Whether the user already raised their hand for this proposal. */
  alreadyInterested: boolean;
  /** Callback when user swipes right / clicks "Raise Hand". */
  onInterested: () => void;
  /** Callback when user swipes left / clicks "Skip". */
  onSkip: () => void;
  /** If true, interest is being submitted (disable interaction). */
  submitting?: boolean;
}

const SWIPE_THRESHOLD = 80;
const TILT_FACTOR = 0.08;

/** Reusable raised-hand inline icon (Heroicons outline style). */
function HandIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d={HAND_ICON_PATH} />
    </svg>
  );
}

export { HandIcon };

/**
 * A Tinder-style card for expressing interest in a session proposal.
 *
 * - Touch: swipe right = raise hand, swipe left = skip
 * - Mouse: drag or click buttons
 * - Keyboard: ArrowRight / Enter = raise hand, ArrowLeft / Escape = skip
 * - After raising hand, a brief micro-animation plays.
 */
export function InterestCard({
  proposal,
  alreadyInterested,
  onInterested,
  onSkip,
  submitting = false,
}: InterestCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [offsetX, setOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showFeedback, setShowFeedback] = useState<'interested' | 'skip' | null>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const isHorizontalSwipe = useRef<boolean | null>(null);

  const peopleLabel =
    proposal.people?.length
      ? proposal.people.map((p) => p.nickname).join(', ')
      : proposal.createdByNickname;

  // ---- Pointer handlers ----
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (submitting || alreadyInterested) return;
      setIsDragging(true);
      startX.current = e.clientX;
      startY.current = e.clientY;
      isHorizontalSwipe.current = null;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [submitting, alreadyInterested],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;
      if (isHorizontalSwipe.current === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        isHorizontalSwipe.current = Math.abs(dx) > Math.abs(dy);
      }
      if (isHorizontalSwipe.current) {
        e.preventDefault();
        setOffsetX(dx);
      }
    },
    [isDragging],
  );

  const commitSwipe = useCallback(
    (direction: 'interested' | 'skip') => {
      setShowFeedback(direction);
      setOffsetX(direction === 'interested' ? 400 : -400);
      setTimeout(() => {
        if (direction === 'interested') onInterested();
        else onSkip();
        setOffsetX(0);
        setShowFeedback(null);
      }, 350);
    },
    [onInterested, onSkip],
  );

  const onPointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    isHorizontalSwipe.current = null;
    if (offsetX > SWIPE_THRESHOLD) commitSwipe('interested');
    else if (offsetX < -SWIPE_THRESHOLD) commitSwipe('skip');
    else setOffsetX(0);
  }, [isDragging, offsetX, commitSwipe]);

  // ---- Keyboard ----
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (submitting || alreadyInterested) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        commitSwipe('interested');
      } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
        e.preventDefault();
        commitSwipe('skip');
      }
    },
    [submitting, alreadyInterested, commitSwipe],
  );

  useEffect(() => {
    cardRef.current?.focus();
  }, [proposal.id]);

  // ---- Visual state ----
  const swipeProgress = Math.min(Math.abs(offsetX) / SWIPE_THRESHOLD, 1);
  const isRight = offsetX > 0;
  const rotation = offsetX * TILT_FACTOR;
  const overlayOpacity = isDragging || showFeedback ? swipeProgress : 0;
  const interestedOverlay = isRight || showFeedback === 'interested' ? overlayOpacity : 0;
  const skipOverlay = (!isRight && offsetX !== 0) || showFeedback === 'skip' ? overlayOpacity : 0;

  const cardStyle: React.CSSProperties = {
    transform: `translateX(${offsetX}px) rotate(${rotation}deg)`,
    transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(.4,0,.2,1)',
    touchAction: 'pan-y',
    willChange: 'transform',
  };

  return (
    <div className="interest-card-container" aria-live="polite">
      <div
        ref={cardRef}
        className="interest-card card"
        style={cardStyle}
        tabIndex={0}
        role="button"
        aria-label={`Session: ${proposal.title} by ${peopleLabel}. Swipe right or press Enter to raise your hand, swipe left or press Escape to skip.`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {/* Swipe overlays */}
        <div className="interest-overlay interest-overlay--right" style={{ opacity: interestedOverlay }} aria-hidden>
          <span className="interest-overlay__label">
            <HandIcon className="w-8 h-8 inline-block mr-1 -mt-1" />
            Raise Hand!
          </span>
        </div>
        <div className="interest-overlay interest-overlay--left" style={{ opacity: skipOverlay }} aria-hidden>
          <span className="interest-overlay__label">Skip</span>
        </div>

        {/* Card content */}
        <div className="relative z-10 p-6 select-none">
          {proposal.displayId != null && (
            <span className="text-sm font-mono text-primary-600 font-semibold">#{proposal.displayId}</span>
          )}
          <h2 className="text-2xl font-bold text-gray-900 mt-1 mb-3">{proposal.title}</h2>
          <p className="text-base text-gray-600 mb-2">by {peopleLabel}</p>
          {proposal.description && (
            <p className="text-sm text-gray-500 line-clamp-3 mb-4">{proposal.description}</p>
          )}
          {alreadyInterested && (
            <div className="interest-done-badge" role="status">
              <HandIcon />
              Hand raised
            </div>
          )}
        </div>
      </div>

      {/* Micro-animation */}
      {showFeedback === 'interested' && (
        <div className="interest-burst" aria-hidden>
          <HandIcon className="w-16 h-16 text-green-500" />
        </div>
      )}

      {/* Desktop buttons */}
      {!alreadyInterested && !showFeedback && (
        <div className="interest-actions">
          <button
            type="button"
            className="interest-btn interest-btn--skip"
            onClick={() => commitSwipe('skip')}
            disabled={submitting}
            aria-label="Skip this session"
            title="Not interested (swipe left or ArrowLeft)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="interest-btn__text">Skip</span>
          </button>
          <button
            type="button"
            className="interest-btn interest-btn--interested"
            onClick={() => commitSwipe('interested')}
            disabled={submitting}
            aria-label="Raise your hand for this session"
            title="Raise hand (swipe right or Enter)"
          >
            <HandIcon className="w-6 h-6" />
            <span className="interest-btn__text">Raise Hand</span>
          </button>
        </div>
      )}

      <p className="interest-keyboard-hint" aria-hidden>
        Use arrow keys or Enter / Escape
      </p>
    </div>
  );
}
